import { Fragment } from "react";

import clsx from "clsx";
import { Link } from "react-router-dom";

import { ControlBar } from "@components/control-bar";
import { useSalesBreakdownConfigs } from "@components/data/sales-breakdown-configs";
import { ExpandableBreakdownSelector } from "@components/reports/breakdown-selector";
import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import { DateRangeChoiceEnum } from "@north-beam/nb-common";
import { formatDateRangeText } from "@shared/date-range-picker";
import {
  AttributionModelSelect,
  DateRangeSelect,
  GranularitySelect,
} from "@shared/selects";
import { renderChevronAnimation } from "@shared/selects/utils";

import { useReportBodyState } from "./use-report-body-control";

export interface ReportBodyControlProps {
  isLoading: boolean;
}

export function ReportBodyControl({ isLoading }: ReportBodyControlProps) {
  const { user } = useUser();
  const { value: breakdownConfigs } = useSalesBreakdownConfigs();
  const {
    attributionModel1,
    setAttributionModel1,

    attributionModel2,
    setAttributionModel2,

    granularity,
    setGranularity,

    breakdowns,
    setBreakdowns,

    state,
    setDateRange,
  } = useReportBodyState();

  const dateRangeCurrent =
    typeof state.dateRange === "string"
      ? DateRangeChoiceEnum.toReactSelect(state.dateRange)
      : state.dateRange;

  return (
    <ControlBar>
      <div className="flex items-center justify-between w-full">
        <div className="flex space-x-2 max-w-7xl" style={{ flex: "3 3 auto" }}>
          <AttributionModelSelect
            label="ATTRIBUTION MODEL 1"
            isLoading={isLoading}
            attributionModel={attributionModel1}
            onChange={setAttributionModel1}
          />
          <AttributionModelSelect
            label="ATTRIBUTION MODEL 2"
            isLoading={isLoading}
            attributionModel={attributionModel2}
            onChange={setAttributionModel2}
          />
          <GranularitySelect
            isLoading={isLoading}
            granularity={granularity}
            hasPlatform={false}
            onChange={setGranularity}
          />
          <ExpandableBreakdownSelector
            label={
              <GenericTooltip
                mouseLeaveDelay={0.1}
                content={
                  <>
                    <p>
                      Breakdowns allow you to organize your marketing and sales
                      efforts in a way that makes sense for your business.
                    </p>
                    <p>Edit a Breakdown by clicking on it.</p>
                    <p>
                      To bulk edit labels for all of your Breakdowns, you can
                      either go to the{" "}
                      <Link to="../objects">Manual Breakdown Label Editor</Link>{" "}
                      page, or the{" "}
                      <Link to="../breakdown-editor">Breakdown Manager</Link>
                      {user.isAdmin && (
                        <>
                          <Link to="../rules">Automatic Label Rules</Link> page
                        </>
                      )}{" "}
                      to set automated labeling rules.
                    </p>
                  </>
                }
              >
                BREAKDOWN BY
              </GenericTooltip>
            }
            isLoading={isLoading}
            current={breakdowns}
            available={breakdownConfigs}
            allConfigs={breakdownConfigs}
            onUpdate={setBreakdowns}
          />
        </div>
        <Popover className="nb-input-container w-52 relative self-end">
          <PopoverButton
            className="nb-input flex flex-col items-start py-0 ml-2"
            disabled={isLoading}
          >
            {({ open }) => (
              <div className="flex items-center space-between w-full h-full">
                <div className="flex-grow text-left">
                  {formatDateRangeText(dateRangeCurrent)}
                </div>
                {renderChevronAnimation(open)}
              </div>
            )}
          </PopoverButton>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <PopoverPanel
              className={clsx(
                "absolute right-0 z-100 mt-2 border w-72",
                "rounded-md p-3 bg-white",
              )}
            >
              <DateRangeSelect
                isLoading={isLoading}
                maxLookbackDays={20 * 365}
                dateRange={state.dateRange}
                onUpdate={setDateRange}
                popoverPlacement="bottom-end"
              />
            </PopoverPanel>
          </Transition>
        </Popover>
      </div>
    </ControlBar>
  );
}
